import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PricingCard from "../components/PricingCard";
import { Server, Cloud, Globe, Zap, Shield, Mail, Check, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const categoryConfig = {
  vps: {
    label: "VPS Hosting", subtitle: "Virtual Private Servers",
    desc: "Full root access, dedicated resources, AMD EPYC processors. Built for developers who need power.",
    icon: Server, color: "#00F3FF", tag: "DEVELOPER READY",
    features: ["Full Root Access & SSH", "AMD EPYC Processors", "NVMe SSD Storage", "DDoS Protection & Firewall", "Free Weekly Backups", "1 Gbps Network Speed", "99.9% Uptime SLA", "24/7 Expert Support"],
    faqs: [
      { q: "What is VPS hosting?", a: "A Virtual Private Server gives you dedicated CPU, RAM, and storage on a shared physical machine. You get full root access, complete control, and consistent performance — unlike shared hosting where resources are split unpredictably." },
      { q: "Do I need technical knowledge?", a: "Basic Linux knowledge helps, but our 24/7 support team and AI assistant (CloudBot) guide you through any task. We also offer optional managed VPS services for complete hands-off management." },
      { q: "Can I upgrade my plan?", a: "Yes! You can upgrade your VPS plan anytime through the control panel with zero downtime. Resources are scaled up instantly." },
      { q: "What operating systems are available?", a: "Ubuntu 20.04 & 22.04, CentOS 7 & 8, Debian 10 & 11, AlmaLinux, Rocky Linux, and more. You get a full choice of OS during setup." },
      { q: "How is bandwidth calculated?", a: "Bandwidth resets every calendar month. Unused bandwidth doesn't roll over. All plans include DDoS protection within the included bandwidth." },
    ],
  },
  shared: {
    label: "Shared Hosting", subtitle: "Web Hosting",
    desc: "The fastest way to launch your website. Perfect for blogs, portfolios, and small businesses.",
    icon: Globe, color: "#00FF94", tag: "BEGINNER FRIENDLY",
    features: ["1-Click App Installer", "Free SSL Certificates", "Free Domain (select plans)", "LiteSpeed Web Server", "AI Website Builder", "Weekly Auto-Backups", "99.9% Uptime", "Free Website Migration"],
    faqs: [
      { q: "What's included in all plans?", a: "All shared hosting plans include free SSL certificates, weekly backups, 1-click installer (WordPress, Joomla, Drupal, etc.), and 24/7 support. Higher plans add more websites, storage, and AI tools." },
      { q: "How many websites can I host?", a: "Ranges from 1 website on the Basic plan to 100 websites on the Cloud Starter plan. You can always upgrade to host more sites." },
      { q: "Is there a free domain included?", a: "The Premium plan and above include a free domain for the first year (your choice of .com, .in, .net, or .org). Renewal pricing applies after year one." },
      { q: "What is the uptime guarantee?", a: "We guarantee 99.9% uptime, backed by an SLA. In the rare case of extended downtime beyond our SLA, you're eligible for service credits." },
      { q: "Can I migrate my existing website?", a: "Yes! Free website migration is included with all plans. Our team handles the entire process with zero downtime and no data loss." },
    ],
  },
  cloud: {
    label: "Cloud Hosting", subtitle: "Managed Cloud",
    desc: "20x more resources than traditional hosting. Dedicated IP, CDN, priority support. Fully managed.",
    icon: Cloud, color: "#BC13FE", tag: "BUSINESS GRADE",
    features: ["4-8 Dedicated CPU Cores", "Up to 16GB RAM", "NVMe SSD Storage", "Included CDN", "Dedicated IP Address", "Daily Backups", "Priority 24/7 Support", "DDoS Protection"],
    faqs: [
      { q: "How is cloud hosting different from shared?", a: "Cloud hosting gives you dedicated CPU cores, guaranteed RAM, a dedicated IP address, and a CDN — resources that are exclusively yours. It's 20x more powerful than shared hosting at a competitive price." },
      { q: "Is it managed or unmanaged?", a: "Fully managed. Our team handles all server maintenance, security patches, and infrastructure updates. You focus on your business, we handle the servers." },
      { q: "Can I scale up instantly?", a: "Yes! Upgrade your cloud plan anytime through the control panel. Resources are provisioned within minutes with no downtime." },
      { q: "Is there a free CDN included?", a: "Yes! All Cloud Hosting plans include a built-in CDN that serves your content from the nearest location to your visitors, reducing load times globally." },
      { q: "What kind of businesses use cloud hosting?", a: "Cloud hosting is ideal for e-commerce stores, SaaS applications, high-traffic blogs, business websites, and any project where performance and reliability are non-negotiable." },
    ],
  },
  wordpress: {
    label: "WordPress Hosting", subtitle: "Managed WordPress",
    desc: "Fully managed WordPress with AI tools, auto-updates, LiteSpeed cache, and WooCommerce support.",
    icon: Zap, color: "#ffcc00", tag: "AI POWERED",
    features: ["1-Click WordPress Install", "AI WordPress Troubleshooter", "AI Website Builder", "LiteSpeed + Object Cache", "Auto WordPress Updates", "Staging Environment", "WooCommerce Ready", "Free CDN (select plans)"],
    faqs: [
      { q: "Is WordPress pre-installed?", a: "Yes! WordPress is pre-installed on your hosting account. You can be live with your website within minutes of signing up." },
      { q: "Do you support WooCommerce?", a: "Absolutely. All plans support WooCommerce. The Business AI plan and above include dedicated e-commerce optimizations for faster checkout and product pages." },
      { q: "Are WordPress updates automatic?", a: "Yes, WordPress core, theme, and plugin updates are automated on Business AI and Cloud AI plans. This keeps your site secure and up-to-date without any manual action." },
      { q: "What AI tools are included?", a: "Business AI and Cloud AI plans include: AI WordPress Troubleshooter (diagnoses and fixes issues), AI Website Builder (create pages with prompts), and AI Email Marketing assistant." },
      { q: "What is the staging environment?", a: "A staging site is a perfect mirror of your live website. Test theme changes, plugin updates, or design tweaks safely before pushing them live. Available on Business AI plans and above." },
    ],
  },
  ssl: {
    label: "SSL Certificates", subtitle: "Website Security",
    desc: "Secure your website and build trust. Free reissues, 256-bit encryption, browser padlock.",
    icon: Shield, color: "#BC13FE", tag: "SECURITY FIRST",
    features: ["256-bit SSL Encryption", "Browser Padlock Display", "Free Certificate Reissues", "Domain Validation DV", "Wildcard & Multi-Domain", "Fast Issuance (5 min)", "24/7 Support", "HTTPS Protection"],
    faqs: [
      { q: "How fast is SSL issuance?", a: "Domain Validated (DV) SSL certificates are issued in under 5 minutes after domain verification. Extended Validation (EV) certificates take 1-3 business days due to business verification." },
      { q: "What's the difference between DV, Wildcard, and EV?", a: "DV SSL secures a single domain. Wildcard SSL secures your domain plus all its subdomains (*.yourdomain.com). EV SSL adds business validation and shows your company name in the address bar for maximum trust." },
      { q: "Is renewal automatic?", a: "We send email reminders 60, 30, and 7 days before expiry. Auto-renewal can be enabled in your account dashboard to ensure your site never shows a security warning." },
      { q: "Can I install the SSL myself?", a: "Yes, full CSR and installation instructions are provided. If you're on IndiaCloud hosting, we handle the installation for you automatically." },
    ],
  },
  email: {
    label: "Email Hosting", subtitle: "Business Email",
    desc: "Professional email with your domain. Anti-spam, webmail, mobile sync, and calendar integration.",
    icon: Mail, color: "#00FF94", tag: "PROFESSIONAL",
    features: ["Custom Domain Email", "Advanced Anti-Spam", "Webmail Access", "Mobile Sync (iOS/Android)", "Calendar & Contacts", "Email Archiving", "Team Collaboration", "99.9% Uptime SLA"],
    faqs: [
      { q: "Can I use my own domain name?", a: "Yes! You'll get professional email addresses like you@yourdomain.com, info@yourdomain.com, etc. It only works if you own the domain (you can register one with us)." },
      { q: "Is webmail included?", a: "Yes, access your email from any browser at webmail.yourdomain.com. We also support all email clients (Outlook, Gmail, Thunderbird) via IMAP/POP3/SMTP." },
      { q: "What spam protection is included?", a: "All plans include advanced anti-spam filtering powered by machine learning. Pro and Business plans get enterprise-grade filtering that blocks 99.9% of spam and phishing emails." },
      { q: "Can I access email on my phone?", a: "Yes! Full support for IMAP and ActiveSync means your emails, calendar, and contacts sync seamlessly to iPhone, Android, and any mobile email app." },
    ],
  },
};

// Comparison table rows per category
const comparisonRows = {
  vps: ["Price", "CPU", "RAM", "Storage", "Bandwidth", "Root Access", "Free Domain", "DDoS Protection"],
  shared: ["Price", "Websites", "Storage", "Mailboxes", "Free SSL", "Free Domain", "Daily Backups", "CDN"],
  cloud: ["Price", "CPU", "RAM", "Storage", "Websites", "Dedicated IP", "CDN", "Backups"],
  wordpress: ["Price", "WP Sites", "Storage", "Mailboxes", "Free Domain", "AI Tools", "Staging", "CDN"],
  ssl: ["Price", "Coverage", "Encryption", "Browser Trust", "Issuance Time", "Free Reissues", "Warranty"],
  email: ["Price", "Mailboxes", "Storage/box", "Anti-Spam", "Webmail", "Calendar", "Archiving", "Support"],
};

function getPlanValue(plan, row) {
  const specMap = { vps: { CPU: "cpu", RAM: "ram", Storage: "storage", Bandwidth: "bandwidth" }, shared: { Websites: "websites", Storage: "storage", Mailboxes: "mailboxes" }, cloud: { CPU: "cpu", RAM: "ram", Storage: "storage", Websites: "websites" }, wordpress: { "WP Sites": "websites", Storage: "storage", Mailboxes: "mailboxes" }, ssl: { Coverage: "domains", Encryption: "type" }, email: { Mailboxes: "mailboxes", "Storage/box": "storage" } };
  if (row === "Price") return `₹${plan.price.toLocaleString("en-IN")}/${plan.price_period === "year" ? "yr" : "mo"}`;
  const cats = specMap[plan.category] || {};
  if (cats[row]) return plan.specs?.[cats[row]] || "—";
  const hasFeature = plan.features?.some(f => f.toLowerCase().includes(row.toLowerCase()));
  return hasFeature ? "check" : (plan.features?.length > 0 ? "x" : "—");
}

function ComparisonTable({ plans, category, color }) {
  const rows = comparisonRows[category] || [];
  if (!plans.length) return null;
  return (
    <div className="overflow-x-auto rounded-2xl" style={{ border: "1px solid rgba(0,243,255,0.1)" }}>
      <table className="w-full min-w-max">
        <thead>
          <tr style={{ background: "rgba(10,10,18,0.9)", borderBottom: "1px solid rgba(0,243,255,0.08)" }}>
            <th className="text-left px-5 py-4 font-rajdhani font-700 text-xs text-gray-500 tracking-wider uppercase w-36">Feature</th>
            {plans.map(p => (
              <th key={p.id} className="px-4 py-4 font-rajdhani font-700 text-sm text-center relative"
                style={{ color: p.highlighted ? color : "#fff" }}>
                {p.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(to right, transparent, ${color}, transparent)` }} />
                )}
                {p.name}
                {p.badge && <div className="text-xs font-outfit mt-0.5" style={{ color }}>{p.badge}</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row} style={{ background: i % 2 === 0 ? "rgba(10,10,18,0.4)" : "rgba(10,10,18,0.15)", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
              <td className="px-5 py-3.5 font-rajdhani font-600 text-sm text-gray-400">{row}</td>
              {plans.map(p => {
                const val = getPlanValue(p, row);
                return (
                  <td key={p.id} className="px-4 py-3.5 text-center" style={p.highlighted ? { background: `${color}05` } : {}}>
                    {val === "check" ? (
                      <Check size={14} className="mx-auto" style={{ color: "#00FF94", filter: "drop-shadow(0 0 4px #00FF9480)" }} />
                    ) : val === "x" ? (
                      <span className="font-outfit text-sm text-gray-700">—</span>
                    ) : (
                      <span className="font-outfit text-sm text-gray-300" style={{ fontWeight: row === "Price" ? 600 : 300, color: row === "Price" && p.highlighted ? color : undefined }}>{val}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FAQSection({ faqs, color }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="flex flex-col gap-3">
      {faqs.map((faq, i) => (
        <div key={i} className="rounded-2xl overflow-hidden transition-all duration-300"
          style={{ background: "rgba(10,10,18,0.8)", border: `1px solid ${open === i ? color + "40" : "rgba(255,255,255,0.05)"}` }}>
          <button data-testid={`faq-item-${i}`} onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between p-5 text-left">
            <span className="font-rajdhani font-700 text-sm text-white pr-4">{faq.q}</span>
            {open === i
              ? <ChevronUp size={16} color={color} className="flex-shrink-0" />
              : <ChevronDown size={16} color="#6b7280" className="flex-shrink-0" />}
          </button>
          {open === i && (
            <div className="px-5 pb-5">
              <div className="h-px mb-4" style={{ background: `linear-gradient(to right, ${color}30, transparent)` }} />
              <p className="font-outfit text-sm text-gray-400 leading-relaxed" style={{ fontWeight: 300 }}>{faq.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const YEARLY_CATEGORIES = ["vps", "shared", "cloud", "wordpress", "email"];

export default function HostingPage({ category }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [showComparison, setShowComparison] = useState(false);
  const config = categoryConfig[category] || categoryConfig.shared;
  const Icon = config.icon;
  const showToggle = YEARLY_CATEGORIES.includes(category);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/plans/category/${category}`);
        setPlans(res.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetch();
    window.scrollTo(0, 0);
    setBillingPeriod("monthly");
  }, [category]);

  return (
    <div data-testid={`hosting-page-${category}`} className="page-enter pt-24">
      {/* Hero Banner */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: `radial-gradient(ellipse at 50% 0%, ${config.color}12 0%, transparent 60%), #030305` }}>
        <div className="animated-grid absolute inset-0 opacity-30" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: `${config.color}12`, border: `1px solid ${config.color}30`, color: config.color }}>
            <Icon size={12} /> {config.tag}
          </div>
          <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-3">{config.label}</h1>
          <div className="font-rajdhani font-600 text-xl mb-4" style={{ color: config.color }}>{config.subtitle}</div>
          <p className="font-outfit text-base text-gray-400 max-w-2xl mx-auto mb-8" style={{ fontWeight: 300 }}>{config.desc}</p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            {["30-Day Money-Back", "99.9% Uptime SLA", "24/7 Support", "Free Migration"].map((badge) => (
              <div key={badge} className="flex items-center gap-1.5 text-sm font-outfit text-gray-400">
                <Check size={13} color={config.color} /> {badge}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Billing Toggle */}
          {showToggle && (
            <div className="flex justify-center mb-10">
              <div className="inline-flex items-center gap-1 p-1 rounded-full"
                style={{ background: "rgba(10,10,18,0.9)", border: "1px solid rgba(0,243,255,0.15)" }}>
                <button data-testid="billing-monthly" onClick={() => setBillingPeriod("monthly")}
                  className="px-5 py-2 rounded-full font-rajdhani font-700 text-sm transition-all duration-300"
                  style={{
                    background: billingPeriod === "monthly" ? "linear-gradient(135deg, #00F3FF, #BC13FE)" : "transparent",
                    color: billingPeriod === "monthly" ? "#030305" : "#9ca3af",
                  }}>
                  Monthly
                </button>
                <button data-testid="billing-yearly" onClick={() => setBillingPeriod("yearly")}
                  className="px-5 py-2 rounded-full font-rajdhani font-700 text-sm transition-all duration-300 flex items-center gap-2"
                  style={{
                    background: billingPeriod === "yearly" ? "linear-gradient(135deg, #00F3FF, #BC13FE)" : "transparent",
                    color: billingPeriod === "yearly" ? "#030305" : "#9ca3af",
                  }}>
                  Yearly
                  <span className="text-xs px-1.5 py-0.5 rounded-full"
                    style={{
                      background: billingPeriod === "yearly" ? "rgba(0,0,0,0.2)" : "rgba(0,255,148,0.15)",
                      color: billingPeriod === "yearly" ? "#030305" : "#00FF94",
                      border: billingPeriod === "yearly" ? "none" : "1px solid rgba(0,255,148,0.3)",
                    }}>
                    SAVE 20%
                  </span>
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl mx-auto mb-4 animate-pulse-glow"
                  style={{ background: "linear-gradient(135deg, #00F3FF22, #BC13FE22)", border: "1px solid rgba(0,243,255,0.3)" }} />
                <p className="font-outfit text-sm text-gray-500">Loading plans...</p>
              </div>
            </div>
          ) : (
            <div className={`grid gap-6 ${plans.length <= 2 ? "md:grid-cols-2 max-w-2xl mx-auto" : plans.length === 3 ? "md:grid-cols-3 max-w-4xl mx-auto" : "md:grid-cols-2 lg:grid-cols-4"}`}>
              {plans.map((plan) => (
                <PricingCard key={plan.id} plan={plan} billingPeriod={billingPeriod} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Plan Comparison Table */}
      {!loading && plans.length > 1 && (
        <section className="py-8 pb-16" style={{ background: "rgba(10,10,18,0.3)" }}>
          <div className="max-w-7xl mx-auto px-6">
            <button data-testid="toggle-comparison" onClick={() => setShowComparison(!showComparison)}
              className="flex items-center gap-2 mx-auto mb-6 px-5 py-2.5 rounded-full font-rajdhani font-700 text-sm transition-all"
              style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)", color: "#00F3FF" }}>
              {showComparison ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showComparison ? "HIDE" : "COMPARE ALL PLANS"}
            </button>
            {showComparison && (
              <div className="animate-fade-in">
                <h2 className="font-orbitron font-700 text-xl text-white text-center mb-6">
                  Plan <span className="text-gradient">Comparison</span>
                </h2>
                <ComparisonTable plans={plans} category={category} color={config.color} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features included */}
      <section className="py-16" style={{ background: "rgba(10,10,18,0.4)" }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-orbitron font-700 text-2xl text-white text-center mb-10">
            Everything <span className="text-gradient">Included</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {config.features.map((feat) => (
              <div key={feat} className="flex items-center gap-3 glass rounded-xl p-4">
                <Check size={15} color={config.color} className="flex-shrink-0" style={{ filter: `drop-shadow(0 0 6px ${config.color}80)` }} />
                <span className="font-outfit text-sm text-gray-300" style={{ fontWeight: 300 }}>{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      {config.faqs && (
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-orbitron font-700 text-2xl text-white text-center mb-10">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <FAQSection faqs={config.faqs} color={config.color} />
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16" style={{ background: "rgba(10,10,18,0.3)" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="rounded-2xl p-10"
            style={{ background: `linear-gradient(135deg, ${config.color}08, transparent)`, border: `1px solid ${config.color}20` }}>
            <h3 className="font-orbitron font-700 text-2xl text-white mb-3">Not Sure Which Plan?</h3>
            <p className="font-outfit text-sm text-gray-500 mb-6" style={{ fontWeight: 300 }}>
              Let our AI analyze your needs and recommend the perfect plan in 60 seconds.
            </p>
            <Link to="/plan-recommender" className="no-underline">
              <button className="btn-primary flex items-center gap-2 mx-auto px-8 py-3 text-sm font-700">
                <Zap size={14} /> AI PICK MY PLAN <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
